import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { categories } from "@/mocks/categories";
import { MAIN_SITE_URL } from "@/constants";

const GUIDE_URL = "/guide/what-is-allchinabuy-spreadsheet";

const sectionLinks = [
  { key: "spreadsheet", section: "what" },
  { key: "categories", section: "categories" },
  { key: "guides", section: "guides" },
  { key: "qc", section: "qc" },
  { key: "faq", section: "faq" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  function goToSection(section: string) {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(section)?.scrollIntoView({ behavior: "smooth" });
      }, 120);
    } else {
      document.getElementById(section)?.scrollIntoView({ behavior: "smooth" });
    }
  }

  return (
    <header className="sticky top-0 z-40 border-b-2 border-background-200 bg-white">
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2" aria-label={t("brand")}>
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-white">
            <i className="ri-table-line text-lg"></i>
          </span>
          <span className="hidden text-sm font-bold leading-tight text-foreground-950 sm:block">
            {t("brand")}
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          <Link
            to="/"
            className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-primary-50 hover:text-primary-700"
          >
            {t("nav.home")}
          </Link>
          {sectionLinks.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => goToSection(item.section)}
              className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-primary-50 hover:text-primary-700"
            >
              {t(`nav.${item.key}`)}
            </button>
          ))}
          <Link
            to={GUIDE_URL}
            className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-primary-50 hover:text-primary-700"
          >
            {t("nav.shoppingGuide")}
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            {t("cta.buyNow")}
          </a>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-background-200 text-foreground-800 lg:hidden"
            aria-label="Menu"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-white px-4 py-4 lg:hidden">
          <nav className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="rounded-md px-3 py-2.5 text-left text-sm font-semibold text-foreground-800 transition-colors hover:bg-primary-50"
            >
              <i className="ri-home-4-line mr-2"></i>
              {t("nav.home")}
            </Link>
            <Link
              to={GUIDE_URL}
              onClick={() => setMobileOpen(false)}
              className="rounded-md px-3 py-2.5 text-left text-sm font-semibold text-foreground-800 transition-colors hover:bg-primary-50"
            >
              <i className="ri-book-open-line mr-2"></i>
              {t("nav.shoppingGuide")}
            </Link>
            {sectionLinks.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => goToSection(item.section)}
                className="rounded-md px-3 py-2.5 text-left text-sm font-semibold text-foreground-800 transition-colors hover:bg-primary-50"
              >
                {t(`nav.${item.key}`)}
              </button>
            ))}
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 inline-flex items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white"
            >
              {t("cta.buyNow")}
            </a>
          </nav>

          <div className="mt-5 border-t border-background-100 pt-4">
            <p className="flex items-center gap-2 px-3 text-xs font-bold uppercase tracking-wide text-foreground-500">
              <i className="ri-grid-line"></i>
              {t("nav.mobileCategories")}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-primary-50"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white">
                    <img src={cat.image} alt="" className="h-full w-full object-cover object-top" />
                  </span>
                  <span className="truncate">{t(`categoryNames.${cat.id}`)}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}